import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  static SKELETON_COUNT = 8;

  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Set<number>} */
  #renderedPages = new Set();

  /** @type {Set<number>} */
  #pagesInFlight = new Set();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#syncRenderedPages();
    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #getViewMoreElements() {
    const grid = this.refs.grid;

    return {
      viewMorePrevious: grid?.querySelector('[ref="viewMorePrevious"]') ?? null,
      viewMoreNext: grid?.querySelector('[ref="viewMoreNext"]') ?? null,
    };
  }

  #createViewMoreObserver() {
    return new IntersectionObserver(
      async (entries) => {
        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            const { viewMorePrevious, viewMoreNext } = this.#getViewMoreElements();

            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else if (entry.target === viewMoreNext) {
              this.#renderNextPage();
            }
          }
        }
      },
      {
        rootMargin: '200px',
      }
    );
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.#getViewMoreElements();

    if (!viewMorePrevious && !viewMoreNext) return;

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    this.infinityScrollObserver = this.#createViewMoreObserver();

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #replaceSkeletonsWithProducts(productElements) {
    const { grid } = this.refs;
    if (!grid) return;

    const skeletons = Array.from(grid.querySelectorAll('.skeleton-item'));
    const products = Array.from(productElements);

    skeletons.forEach((skeleton, index) => {
      const product = products[index];

      if (skeleton.getAttribute('ref') === 'viewMoreNext') {
        this.infinityScrollObserver?.unobserve(skeleton);
      }

      if (product) {
        skeleton.replaceWith(product);
      } else {
        skeleton.remove();
      }
    });

    if (products.length > skeletons.length) {
      grid.append(...products.slice(skeletons.length));
    }
  }

  #createSkeletonItem(refName) {
    const element = document.createElement('li');
    element.className = 'product-grid__item resource-list__item skeleton-item';

    if (refName) {
      element.setAttribute('ref', refName);
    }

    element.innerHTML = `
      <div
        class="skeleton-card-gallery skeleton-shimmer"
        style="width: 100%; aspect-ratio: 1 / 1; margin-bottom: 12px;"
      ></div>
      <div
        class="skeleton-title-container"
        style="margin-bottom: 4px; height: 40px; display: flex; flex-direction: column; justify-content: flex-start; gap: 0;"
      >
        <div class="skeleton-text skeleton-shimmer" style="height: 20px; width: 90%;"></div>
        <div class="skeleton-text skeleton-shimmer" style="height: 20px; width: 60%;"></div>
      </div>
      <div
        class="skeleton-price-container"
        style="height: 25px; display: flex; align-items: baseline; gap: 6px;"
      >
        <div class="skeleton-text skeleton-shimmer" style="height: 18px; width: 80px;"></div>
      </div>
    `;

    return element;
  }

  #appendSkeletons(count) {
    const { grid } = this.refs;
    if (!grid || count <= 0) return;

    /** @type {HTMLElement | null} */
    let viewMoreSkeleton = null;

    for (let index = 0; index < count; index += 1) {
      const isViewMore = index === 0;
      const skeleton = this.#createSkeletonItem(isViewMore ? 'viewMoreNext' : undefined);
      grid.append(skeleton);

      if (isViewMore) {
        viewMoreSkeleton = skeleton;
      }
    }

    if (viewMoreSkeleton) {
      this.infinityScrollObserver?.observe(viewMoreSkeleton);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage) || !this.#reservePage(nextPage.page)) return;

    try {
      await this.#insertNextPage(nextPage);
    } finally {
      this.#pagesInFlight.delete(nextPage.page);
    }
  }

  /**
   * @param {{ page: number, url: URL }} nextPage
   */
  async #insertNextPage(nextPage) {
    const { grid } = this.refs;
    if (!grid) return;

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    const freshItems = this.#withoutExistingCards(nextPageItemElements);
    this.#renderedPages.add(nextPage.page);
    if (!freshItems.length) return;

    this.#replaceSkeletonsWithProducts(freshItems);

    const nextPageCount = freshItems.length;
    const lastPage = Number(grid.dataset.lastPage);

    if (nextPage.page < lastPage && nextPageCount > 0) {
      this.#appendSkeletons(PaginatedList.SKELETON_COUNT);
      this.#observeViewMore();
    }

    this.#aspectRatioHelper?.processNewElements();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage) || !this.#reservePage(previousPage.page)) return;

    try {
      await this.#insertPreviousPage(previousPage);
    } finally {
      this.#pagesInFlight.delete(previousPage.page);
    }
  }

  /**
   * @param {{ page: number, url: URL }} previousPage
   */
  async #insertPreviousPage(previousPage) {
    const { grid } = this.refs;
    if (!grid) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    const freshItems = this.#withoutExistingCards(previousPageItemElements);
    this.#renderedPages.add(previousPage.page);
    if (!freshItems.length) return;

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...freshItems);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  /**
   * @param {number} page
   * @returns {boolean} False when this page is already shown or already loading.
   */
  #reservePage(page) {
    if (this.#renderedPages.has(page) || this.#pagesInFlight.has(page)) return false;
    this.#pagesInFlight.add(page);
    return true;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const edgePage = this.#edgePage(type);
    if (!edgePage) return;

    const page = type === 'previous' ? edgePage - 1 : edgePage + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * Page numbers already painted in the grid.
   * @returns {number[]}
   */
  #visibleCardPages() {
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"][data-page]'), (card) =>
      Number(card.getAttribute('data-page'))
    ).filter((page) => page > 0);
  }

  /**
   * Lowest page when loading upward, highest page when loading downward.
   * @param {"previous" | "next"} type
   * @returns {number}
   */
  #edgePage(type) {
    const pages = this.#visibleCardPages();
    this.#renderedPages.forEach((page) => pages.push(page));
    if (!pages.length) return 0;

    return type === 'previous' ? Math.min(...pages) : Math.max(...pages);
  }

  #syncRenderedPages() {
    this.#visibleCardPages().forEach((page) => this.#renderedPages.add(page));
  }

  /**
   * Keeps a product that is listed twice in the collection, and drops a second copy of the same slot.
   * @param {ArrayLike<Element>} elements
   * @returns {Element[]}
   */
  #withoutExistingCards(elements) {
    const grid = this.querySelector('[ref="grid"]');
    const existingIndexes = new Set(
      grid
        ? Array.from(grid.querySelectorAll(':scope > [ref="cards[]"][data-product-index]'), (card) =>
            card.classList.contains('skeleton-item') ? '' : card.getAttribute('data-product-index')
          ).filter(Boolean)
        : []
    );

    return Array.from(elements).filter((element) => {
      const index = element.getAttribute('data-product-index');
      if (index && existingIndexes.has(index)) return false;
      if (index) existingIndexes.add(index);
      return true;
    });
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#renderedPages.clear();
    this.#pagesInFlight.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#syncRenderedPages();
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
